import React, {useEffect, useMemo, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import Styles from './index.module.scss'
import NFTModal from "@/components/NFTModal";
import ActivityRules from "../components/ActivityRules";
import RecycleConfirmModal from "../components/RecycleConfirmModal";
import RecycleSuccessModal from "../components/RecycleSuccessModal";
import Service from "../service"
import {hideLoading, showLoading} from "@/components/loading";
import {Toast} from "antd-mobile";

/**
 * @description 宝石回收
 * @return {/culturalExchange_gemstonerecycling/home}
 */
const Btns = [
  { title: '活动规则', key: 1 }, { title: '实名认证', key: 2 },
]
const padTime = (n) => (n >= 10 ? String(n) : `0${n}`)

const formatPointPool = (value, length = 7) => {
  if (value === null || value === undefined || value === '') {
    return '0'.repeat(length)
  }
  const numValue = typeof value === 'string' ? parseInt(value, 10) : value
  if (isNaN(numValue)) {
    return '0'.repeat(length)
  }
  return String(numValue).padStart(length, '0')
}

const formatPool = (value) => {
  const num = Number(value)
  if (!Number.isFinite(num) || num <= 0) return { num: 0, unit: '万' }
  return { num, unit: '万' }
}

const Home = () => {
  const pageDom = useRef(null);
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState({});
  const [data, setData] = useState({});
  const [countTime, setCountTime] = useState({ day: '00', hour: '00', minute: '00', second: '00' })
  const countTimerRef = useRef(null)
  const countStampRef = useRef(null)

  useEffect(() => {
    getData(true);
    onDidFocus();
  }, []);

  const onDidFocus = () => {
    window.JsBridge.addListener().then(() => {
      getData(true)
      onDidFocus();
    });
  }

  const getData = async (loading = false) => {
    try {
      if (loading) showLoading();
      const [res, userInfoRes] = await Promise.all([
        Service.gemsInit(),
        loading
          ? Service.getUserCertificateStatus().catch(() => ({}))
          : Promise.resolve(null),
      ]);
      if (loading) {
        setUserInfo(userInfoRes || {});
        hideLoading();
      }
      if (res.code !== '000000') throw res.msg;
      setData(res.data || {});
    } catch (error) {
      hideLoading();
      Toast.show(error);
    }
  }

  const onBack = () => {
    if (window.JsBridge.hasWebViewBridge()) {
      window.JsBridge.back();
    } else {
      navigate(-1);
    }
  }

  const tipsMessage = useMemo(() => {
    const { startTime, endTime } = data || {};
    if (!startTime || !endTime) return undefined;
    const currentT = Date.now();
    const startT = Date.parse(String(startTime).replace(/-/g, "/"));
    const endT = Date.parse(String(endTime).replace(/-/g, "/"));
    if (!startT || !endT) return "接口异常";
    if (currentT < startT) return "活动未开始";
    if (currentT > endT) return "活动已结束";
    return undefined;
  }, [data]);

  useEffect(() => {
    const endTime = data?.drawTime
    const tick = (currentStamp, endStamp) => {
      const distance = endStamp - currentStamp
      if (distance <= 0) {
        return { day: '00', hour: '00', minute: '00', second: '00', ended: true }
      }
      const remainAllSeconds = Math.floor(distance / 1000)
      return {
        day: padTime(Math.floor(remainAllSeconds / 86400)),
        hour: padTime(Math.floor(remainAllSeconds / 3600) % 24),
        minute: padTime(Math.floor(remainAllSeconds / 60) % 60),
        second: padTime(remainAllSeconds % 60),
        ended: false,
      }
    }
    if (countTimerRef.current) clearInterval(countTimerRef.current)
    if (!endTime) {
      setCountTime({ day: '00', hour: '00', minute: '00', second: '00' })
      return
    }
    countStampRef.current = Date.now()
    const endStamp = Date.parse(String(endTime).replace(/-/g, '/'))
    const first = tick(countStampRef.current, endStamp)
    setCountTime(first)
    if (first.ended) return
    countTimerRef.current = setInterval(() => {
      countStampRef.current += 1000
      const next = tick(countStampRef.current, endStamp)
      setCountTime(next)
      if (next.ended && countTimerRef.current) {
        clearInterval(countTimerRef.current)
      }
    }, 1000)
    return () => {
      if (countTimerRef.current) clearInterval(countTimerRef.current)
    }
  }, [data?.drawTime])

  const holdCount = data?.gemsCount ?? 0
  const joinDisabled = useMemo(() => {
    if (Number(holdCount) < 10) return true
    return tipsMessage === '活动未开始' || tipsMessage === '活动已结束'
  }, [holdCount, tipsMessage])

  const recycledDigits = formatPointPool(data?.gemsPool)
  const prizePool = formatPool(data?.pointTotal)

  const onRightBtnClick = (item) => {
    if (item.key === 1) {
      const ModalInstance = NFTModal.show({
        content: <ActivityRules onClose={() => ModalInstance?.close()} />,
        getContainer: pageDom.current,
      });
    } else if (item.key === 2) {
      window.JsBridge.navigate('Verify');
    }
  }

  const goRecord = () => {
    const recId = data?.recId
    const uri = recId
      ? `/culturalExchange_gemstonerecycling/recycleRecord?recId=${recId}`
      : `/culturalExchange_gemstonerecycling/recycleRecord`
    if (window.JsBridge.hasWebViewBridge()) {
      window.JsBridge.push('CustomWeb', { uri: `${window.location.origin}${uri}` })
    } else {
      navigate(uri)
    }
  }

  const joinHandle = () => {
    if (tipsMessage === '活动未开始' || tipsMessage === '活动已结束') {
      Toast.show(tipsMessage)
      return
    }
    if (!Service.isUserVerified(userInfo)) {
      Toast.show('请先实名认证')
      return
    }
    if (Number(holdCount) < 10) {
      Toast.show('不足10个')
      return
    }
    if (!data?.recId) {
      Toast.show('活动信息异常，请稍后重试')
      return
    }
    const ModalInstance = NFTModal.show({
      content: (
        <RecycleConfirmModal
          holdCount={holdCount}
          onClose={() => ModalInstance?.close()}
          onConfirm={async (count) => {
            try {
              showLoading()
              const res = await Service.gemsRec({ recId: data.recId, count })
              hideLoading()
              if (res.code !== '000000') throw res.msg || '回收失败'
              ModalInstance?.close()
              setData((prev) => ({
                ...prev,
                gemsCount: res.data?.gemsCount ?? prev.gemsCount,
                gemsPool: res.data?.gemsPool ?? prev.gemsPool,
              }))
              const SuccessModal = NFTModal.show({
                content: <RecycleSuccessModal onClose={() => SuccessModal?.close()} />,
                getContainer: pageDom.current,
              })
            } catch (error) {
              hideLoading()
              Toast.show(error?.message || error || '回收失败')
            }
          }}
        />
      ),
      getContainer: pageDom.current,
    })
  }

  return (
    <div className={Styles.culturalExchangeHome} ref={pageDom}>
      <div className={Styles.topCard}></div>
      <div className={Styles.back} onClick={onBack}></div>
      <div className={Styles.right_btns}>
        {
          Btns.map((btn, i) => {
            if (btn.key === 2 && Service.isUserVerified(userInfo)) return null;
            return (
              <div key={i} className={Styles.rightBtn} onClick={() => onRightBtnClick(btn)}>{btn.title}</div>
            )
          })
        }
      </div>

      <div className={Styles.machineBox}>
        <div className={Styles.machineInner}>
          <img
            className={Styles.machineBg}
            src={require('@/assets/img/culturalExchange_gemstonerecycling/gemstone.png')}
            alt=""
          />
          <div className={Styles.recordLink} onClick={goRecord}>回收记录&gt;</div>
          <span className={Styles.countBoxDay}>{countTime.day}</span>
          <span className={Styles.countBoxHour}>{countTime.hour}</span>
          <span className={Styles.countBoxMinute}>{countTime.minute}</span>
          <span className={Styles.countBoxSecond}>{countTime.second}</span>
          <div className={Styles.digitRow}>
            {recycledDigits.split('').map((digit, index) => (
              <span key={index} className={Styles.digit}>{digit}</span>
            ))}
          </div>
          <div className={Styles.statsRow}>
            <div className={Styles.statItem}>
              <span className={Styles.statLabel}>持有宝石数量：</span>
              <span className={Styles.statNum}>{holdCount}</span>
              <span className={Styles.statUnit}>个</span>
            </div>
            <div className={Styles.statItem}>
              <span className={Styles.statLabel}>奖池积分总量：</span>
              <span className={Styles.statNum}>{prizePool.num}{prizePool.unit}</span>
            </div>
          </div>
          <div
            className={`${Styles.joinBtn} ${joinDisabled ? Styles.joinBtnDisabled : ''}`}
            onClick={joinHandle}
          />
        </div>
      </div>

      <div className={Styles.footerBox}>
        <div className={Styles.footer}></div>
      </div>
    </div>
  )
}
export default Home
